import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Chip } from "@mui/material";
import Dropdown from "../dropdown";
import CustomiseTable from "./customiseTable";
import MoreFilters from "./moreFilters";
import styles from "./filter.module.css";
import customTable from '../../../../assets/images/custom-table-icon.svg'
import moreFilters from '../../../../assets/images/more-filters.svg'
import Filters from '../../../../assets/images/more-filters-icon.svg'
import Refresh from '../../../../assets/images/refresh-icon.svg'
import { getInterlinkagesMasterData, saveFilterPreference } from "Samarth Module/api/api";
import { readCachedMaster, writeCachedMaster, fetchMasterDataDedup, readFavorites, writeFavorites } from "Samarth Module/api/filterMasterCache";

const TOP_BAR_LIMIT = 8;
const DEFAULT_FILTER_TYPE = "RMPM Alert";

const getUserEmail = () => {
    try {
        return JSON.parse(localStorage.getItem("UserInfo"))?.user_info?.email || "";
    } catch (error) {
        console.error("Failed to read user email from localStorage:", error?.message);
        return "";
    }
};
const EMPTY_FILTERS = [];
const EMPTY_SELECTION = [];

const getInitialValues = (filters) => (
    filters.reduce((acc, filter) => {
        acc[filter.key] = filter.value ?? filter.defaultValue ?? [];
        return acc;
    }, {})
);
const buildSelectedFiltersPayload = (values) => {
    const result = {};
    Object.keys(values || {}).forEach((key) => {
        const arr = Array.isArray(values[key]) ? values[key] : [];
        if (arr.length > 0) result[key] = arr;
    });
    return result;
};

const extractFiltersFromResponse = (res) => {
    if (res?.filters && typeof res.filters === "object") return res.filters;
    if (res?.data?.filters && typeof res.data.filters === "object") return res.data.filters;
    return null;
};
const filterSelectionFingerprint = (values) => {
    const entries = Object.entries(values || {})
        .filter(([, v]) => Array.isArray(v) && v.length > 0)
        .map(([k, v]) => [k, [...v].sort((a, b) => {
            if (a === b) return 0;
            return a < b ? -1 : 1;
        })]);
    entries.sort(([a], [b]) => String(a).localeCompare(String(b)));
    return JSON.stringify(entries);
};

const buildFilterChips = (values, favoriteKeys, onFilterDelete) => {
    const favoriteSet = new Set(Array.isArray(favoriteKeys) ? favoriteKeys : []);
    const chips = [];

    Object.entries(values || {}).forEach(([filterKey, selected]) => {
        if (favoriteSet.has(filterKey) || !Array.isArray(selected) || selected.length === 0) {
            return;
        }

        selected.forEach((value, index) => {
            chips.push({
                key: `${filterKey}-${String(value)}-${index}`,
                label: value,
                onDelete: () => onFilterDelete(filterKey, index),
            });
        });
    });

    return chips;
};

function Filter({
    filters: filtersProp,
    values,
    onChange,
    onApply,
    onOrderChange,
    onRefresh,
    onMoreClick,
    onLoadingChange,
    onColumnApply,
    columnPreferences,
    filterType,
}) {
    const [apiFilters, setApiFilters] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [favoriteKeys, setFavoriteKeys] = useState(null);
    const [moreMenuOpen, setMoreMenuOpen] = useState(false);
    const [customiseOpen, setCustomiseOpen] = useState(false);
    const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
    const moreMenuRef = useRef(null);

    // Report filter-options loading state up so the page can show one unified loader.
    useEffect(() => {
        onLoadingChange?.(isLoading);
    }, [isLoading, onLoadingChange]);

    useEffect(() => {
        let cancelled = false;
        const email = getUserEmail();
        if (!email) {
            setIsLoading(false);
            return undefined;
        }
        setFavoriteKeys(null);

        const cached = readCachedMaster(filterType);
        const cachedFilters = cached?.data?.filters;
        const hasCached = cachedFilters && typeof cachedFilters === "object";
        if (hasCached) {
            console.debug(`[Filter] cache HIT for ${filterType}${cached.stale ? " (stale, revalidating)" : ""}`);
            setApiFilters(cachedFilters);
            setIsLoading(false);
        } else {
            setApiFilters(null);
            setIsLoading(true);
        }
        if (!hasCached || cached.stale) {
            console.debug(`[Filter] ${hasCached ? "revalidating" : "fetching"} ${filterType}`);
            fetchMasterDataDedup(filterType, email)
                .then((res) => {
                    const next = res?.filters && typeof res.filters === "object" ? res.filters : null;
                    if (next) writeCachedMaster(filterType, res);
                    if (cancelled) return;
                    if (next) setApiFilters(next);
                    setIsLoading(false);
                })
                .catch((err) => {
                    console.error("Filter options fetch failed:", err?.message);
                    if (!cancelled) setIsLoading(false);
                });
        }

        return () => {
            cancelled = true;
        };
    }, [filterType]);

    const fetchFilteredDropdowns = async (currentValues) => {
        const email = getUserEmail();
        if (!email) return;
        const selected_filters = buildSelectedFiltersPayload(currentValues);
        if (Object.keys(selected_filters).length === 0) {
            const cached = readCachedMaster(filterType);
            if (cached?.data?.filters && typeof cached.data.filters === "object" && !cached.stale) {
                console.debug(`[Filter] cache HIT for ${filterType} (clear all)`);
                setApiFilters(cached.data.filters);
                return;
            }
            try {
                console.debug(`[Filter] cache MISS for ${filterType} (clear all) — fetching`);
                const res = await fetchMasterDataDedup(filterType, email);
                const next = res?.filters && typeof res.filters === "object" ? res.filters : null;
                if (next) {
                    writeCachedMaster(filterType, res);
                    setApiFilters(next);
                }
            } catch (err) {
                console.error("Filter master reload failed:", err?.message);
            }
            return;
        }

        try {
            const res = await getInterlinkagesMasterData(
                encodeURIComponent(filterType),
                { selected_filters, userEmail: email }
            );
            const next = extractFiltersFromResponse(res);
            if (next) {
                setApiFilters((prev) => (prev ? { ...prev, ...next } : next));
            }
        } catch (err) {
            console.error("Filter interlinkage fetch failed:", err?.message);
        }
    };
    useEffect(() => {
        if (apiFilters && favoriteKeys === null) {
            const allKeys = Object.keys(apiFilters);
            const stored = readFavorites(filterType, getUserEmail());
            const validStored = Array.isArray(stored)
                ? stored.filter((key) => allKeys.includes(key))
                : [];
            setFavoriteKeys(
                validStored.length
                    ? validStored.slice(0, TOP_BAR_LIMIT)
                    : allKeys.slice(0, TOP_BAR_LIMIT)
            );
        }
    }, [apiFilters, favoriteKeys, filterType]);
    const apiFilterList = useMemo(() => {
        if (!apiFilters || !favoriteKeys) return null;
        const favSet = new Set(favoriteKeys);
        return Object.keys(apiFilters)
            .filter((key) => favSet.has(key))
            .slice(0, TOP_BAR_LIMIT)
            .map((key) => ({
                key,
                label: key,
                placeholder: "All",
                options: Array.isArray(apiFilters[key]) ? apiFilters[key] : [],
            }));
    }, [apiFilters, favoriteKeys]);

    const filters = useMemo(() => {
        if (filtersProp) return filtersProp;
        if (apiFilterList) return apiFilterList;
        return EMPTY_FILTERS;
    }, [filtersProp, apiFilterList]);
    const persistFavorites = (favKeys) => {
        const email = getUserEmail();
        if (!email || !apiFilters) return;
        writeFavorites(filterType, email, favKeys);
        const favSet = new Set(favKeys);
        const filtersPayload = Object.keys(apiFilters).map((label) => ({
            label,
            isFavourite: favSet.has(label),
        }));
        saveFilterPreference(email, filterType, filtersPayload).catch((err) => {
            console.error("Failed to save filter favorites:", err?.message);
        });
    };

    const handleMoreFiltersApply = ({
        favoriteKeys: nextFavorites,
        selectedValues: nextSelectedValues,
    } = {}) => {
        if (Array.isArray(nextFavorites)) {
            const changed = filterSelectionFingerprint({ fav: nextFavorites })
                !== filterSelectionFingerprint({ fav: favoriteKeys || [] });
            setFavoriteKeys(nextFavorites);
            if (changed) persistFavorites(nextFavorites);
        }

        if (!nextSelectedValues || typeof nextSelectedValues !== "object") return;
        const merged = { ...selectedValues, ...nextSelectedValues };
        if (filterSelectionFingerprint(merged) === filterSelectionFingerprint(selectedValues)) {
            return;
        }

        if (!values) setLocalValues(merged);
        onApply?.(null, null, null, merged);
        fetchFilteredDropdowns(merged);
    };

    const initialValues = useMemo(() => getInitialValues(filters), [filters]);
    const [localValues, setLocalValues] = useState(initialValues);
    useEffect(() => {
        if (values) return;
        setLocalValues((prev) => {
            const next = {};
            Object.keys(initialValues).forEach((key) => {
                next[key] = Array.isArray(prev?.[key]) && prev[key].length > 0
                    ? prev[key]
                    : initialValues[key];
            });
            return next;
        });
    }, [initialValues, values]);

    const selectedValues = values ?? localValues;

    useEffect(() => {
        if (!moreMenuOpen) return undefined;
        const handleClickOutside = (event) => {
            if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
                setMoreMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [moreMenuOpen]);

    const handleChange = (filterKey, nextValues, orderedOptions) => {
        if (!values) {
            setLocalValues((currentValues) => ({
                ...currentValues,
                [filterKey]: nextValues,
            }));
        }

        onChange?.(filterKey, nextValues, orderedOptions);
    };
    const handleApply = (filterKey, nextValues, orderedOptions) => {
        const mergedValues = { ...selectedValues, [filterKey]: nextValues };
        if (!values) setLocalValues(mergedValues);
        onApply?.(filterKey, nextValues, orderedOptions, mergedValues);
        fetchFilteredDropdowns(mergedValues);
    };

    const handleRefresh = () => {
        const clearedValues = filters.reduce((acc, filter) => {
            acc[filter.key] = [];
            return acc;
        }, {});

        if (!values) {
            setLocalValues(clearedValues);
        }

        filters.forEach((filter) => {
            onChange?.(filter.key, [], filter.options);
            onApply?.(filter.key, [], filter.options, clearedValues);
        });

        onRefresh?.(clearedValues);
        fetchFilteredDropdowns(clearedValues);
    };

    const handleChipDelete = (filterKey, valueIndex) => {
        const current = Array.isArray(selectedValues?.[filterKey]) ? selectedValues[filterKey] : [];
        const nextValues = current.filter((_, index) => index !== valueIndex);
        const mergedValues = { ...selectedValues, [filterKey]: nextValues };
        const orderedOptions = Array.isArray(apiFilters?.[filterKey]) ? apiFilters[filterKey] : [];

        if (!values) {
            setLocalValues(mergedValues);
        }

        onChange?.(filterKey, nextValues, orderedOptions);
        onApply?.(filterKey, nextValues, orderedOptions, mergedValues);
        fetchFilteredDropdowns(mergedValues);
    };

    const filterChips = useMemo(
        () => buildFilterChips(selectedValues, favoriteKeys, handleChipDelete),
        [selectedValues, favoriteKeys]
    );

    return (
        <Box className={styles.filterWrapper}>
            <Box
                className={styles.filterBar}
                sx={{
                    gridTemplateColumns: `repeat(${Math.max(filters.length, 1)}, minmax(0, 1fr)) auto`,
                }}
            >
                {filters.map((filter) => (
                    <Dropdown
                        key={filter.key}
                        label={filter.label}
                        options={filter.options}
                        value={selectedValues[filter.key] ?? EMPTY_SELECTION}
                        placeholder={filter.placeholder ?? "All"}
                        width={filter.width ?? "100%"}
                        autoApply
                        onChange={(nextValues, orderedOptions) => handleChange(filter.key, nextValues, orderedOptions)}
                        onApply={(nextValues, orderedOptions) => handleApply(filter.key, nextValues, orderedOptions)}
                        onOrderChange={(orderedOptions) => onOrderChange?.(filter.key, orderedOptions)}
                    />
                ))}

                <Box className={styles.actions}>
                    <button
                        className={styles.iconButton}
                        type="button"
                        onClick={handleRefresh}
                        aria-label="Refresh filters"
                        title="Refresh filters"
                    >
                        <img src={Refresh} alt="Refresh" className={styles.actionIcon} />
                    </button>
                    <Box className={styles.moreContainer} ref={moreMenuRef}>
                        <button
                            className={styles.iconButton}
                            type="button"
                            onClick={() => {
                                setMoreMenuOpen((current) => !current);
                                onMoreClick?.();
                            }}
                            aria-label="More filter options"
                            aria-expanded={moreMenuOpen}
                            title="More filter options"
                        >
                            <img src={Filters} alt="Refresh" className={styles.actionIcon} />
                        </button>

                        {moreMenuOpen && (
                            <Box className={styles.moreMenu} role="menu">
                                <button
                                    className={`${styles.moreMenuItem} ${styles.moreMenuItemActive}`}
                                    type="button"
                                    role="menuitem"
                                    onClick={() => {
                                        setMoreMenuOpen(false);
                                        setCustomiseOpen(true);
                                    }}
                                >
                                    <img src={customTable} alt="Customise table" className={styles.itemIcon} />
                                    <span>Customise Table</span>
                                </button>
                                <button
                                    className={styles.moreMenuItem}
                                    type="button"
                                    role="menuitem"
                                    onClick={() => {
                                        setMoreMenuOpen(false);
                                        setMoreFiltersOpen(true);
                                    }}
                                >
                                    <img src={moreFilters} alt="More filters" className={styles.itemIcon} />
                                    <span>More Filters</span>
                                </button>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>

            <Box className={styles.filtersBtnContainer}>
                <Box className={styles.chipContainer}>
                    {filterChips.map((chip) => (
                        <Chip key={chip.key} label={chip.label} onDelete={chip.onDelete} />
                    ))}
                </Box>
            </Box>

            <CustomiseTable
                open={customiseOpen}
                onClose={() => setCustomiseOpen(false)}
                onApply={onColumnApply}
                columnPreferences={columnPreferences}
                type={filterType}
            />
            <MoreFilters
                open={moreFiltersOpen}
                onClose={() => setMoreFiltersOpen(false)}
                apiFilters={apiFilters}
                favoriteKeys={favoriteKeys}
                favoriteLimit={TOP_BAR_LIMIT}
                selectedValues={selectedValues}
                onApply={handleMoreFiltersApply}
            />
        </Box>
    );
}

Filter.propTypes = {
    filters: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string,
            placeholder: PropTypes.string,
            width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            options: PropTypes.array,
            value: PropTypes.array,
            defaultValue: PropTypes.array,
        })
    ),
    values: PropTypes.objectOf(PropTypes.array),
    onChange: PropTypes.func,
    onApply: PropTypes.func,
    onOrderChange: PropTypes.func,
    onRefresh: PropTypes.func,
    onMoreClick: PropTypes.func,
    onLoadingChange: PropTypes.func,
    onColumnApply: PropTypes.func,
    columnPreferences: PropTypes.object,
    filterType: PropTypes.string,
};

Filter.defaultProps = {
    filters: undefined,
    values: undefined,
    onChange: undefined,
    onApply: undefined,
    onOrderChange: undefined,
    onRefresh: undefined,
    onMoreClick: undefined,
    onLoadingChange: undefined,
    onColumnApply: undefined,
    columnPreferences: undefined,
    filterType: DEFAULT_FILTER_TYPE,
};

export default Filter;